import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from "lucide-react";

export const MarketingChannelGuideModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="fixed top-0 right-0 bottom-0 z-[101] w-[50%] max-w-[960px] bg-slate-900 shadow-2xl flex flex-col border-l border-slate-700"
      >
        {/* Header - gradient bar */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0 bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-600">
          <div className="flex items-center gap-3 min-w-0 mr-4">
            <button onClick={onClose} className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0 font-medium" data-testid="close-channel-guide">
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back to Report</span>
            </button>
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">📊</span>
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">Marketing Channel Guide</p>
              <p className="text-white/60 text-xs">Where each channel fits in your customer's journey</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15 transition-colors">
            <X className="w-4 h-4 text-white/80" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Top of Funnel */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                <span className="text-xl">🌱</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-violet-400">Top of Funnel: Brand Awareness</h3>
                <p className="text-slate-400 text-xs">Casting the widest net • Building name recognition • Future customer pool</p>
              </div>
            </div>
            <div className="bg-violet-500/10 rounded-xl p-4 border border-violet-500/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { emoji: '📺', name: 'CTV', desc: 'Premium brand storytelling, broad reach (60-80% of target market)' },
                  { emoji: '📡', name: 'Local TV', desc: 'Traditional broadcast reach for local market awareness' },
                  { emoji: '📻', name: 'Internet Radio', desc: 'Audio branding, repetition during commutes and work' },
                  { emoji: '🎙️', name: 'Local Radio', desc: 'Local market awareness, community connection' },
                  { emoji: '🪧', name: 'Billboard', desc: 'High-visibility, passive brand exposure along commute routes' },
                  { emoji: '📰', name: 'Print', desc: 'Brand presence in trusted local publications' },
                  { emoji: '🎵', name: 'TikTok', desc: 'Viral potential, discovery-focused, younger demographics' },
                  { emoji: '🎧', name: 'Podcast', desc: 'Engaged audiences, brand storytelling, trust-building' },
                ].map(ch => (
                  <div key={ch.name} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{ch.emoji}</span>
                      <span className="text-white font-medium text-sm">{ch.name}</span>
                    </div>
                    <p className="text-slate-400 text-xs">{ch.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Middle of Funnel */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <span className="text-xl">🤝</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-emerald-400">Middle of Funnel: Consideration & Nurturing</h3>
                <p className="text-slate-400 text-xs">Building relationships • Education & value • Moving from awareness to trust</p>
              </div>
            </div>
            <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { emoji: '✉️', name: 'Email', desc: 'Personalized nurture sequences, educational content (17-39% lead-to-MQL)' },
                  { emoji: '📬', name: 'Direct Mail', desc: 'Tangible touchpoints, personalized offers, cuts through digital noise' },
                  { emoji: '🌐', name: 'Organic Social', desc: 'Community engagement, social proof, relationship building' },
                  { emoji: '⭐', name: 'Loyalty App Programs', desc: 'Reward repeat customers, drive retention, build brand advocates' },
                ].map(ch => (
                  <div key={ch.name} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{ch.emoji}</span>
                      <span className="text-white font-medium text-sm">{ch.name}</span>
                    </div>
                    <p className="text-slate-400 text-xs">{ch.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Bottom of Funnel */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <span className="text-xl">🎯</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-400">Bottom of Funnel: Conversion & Purchase Intent</h3>
                <p className="text-slate-400 text-xs">High intent searches • Ready to buy • The moment of truth</p>
              </div>
            </div>
            <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { emoji: '🔍', name: 'Google Ads', desc: 'High-intent search queries, "buy now" keywords (30-50% of budget)' },
                  { emoji: '🎯', name: 'Retargeting', desc: 'Re-engage warm audiences, cart abandoners, website visitors' },
                  { emoji: '🔎', name: 'SEO/Search', desc: 'Organic visibility for brand and product queries' },
                ].map(ch => (
                  <div key={ch.name} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{ch.emoji}</span>
                      <span className="text-white font-medium text-sm">{ch.name}</span>
                    </div>
                    <p className="text-slate-400 text-xs">{ch.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Full-Funnel Channels */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <span className="text-xl">🔄</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-400">Full-Funnel Channels: Spans All Stages</h3>
                <p className="text-slate-400 text-xs">These channels can serve awareness, consideration, OR conversion depending on campaign setup</p>
              </div>
            </div>
            <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { emoji: '📘', name: 'Paid Facebook Ads', desc: 'Awareness campaigns → Retargeting → Conversion ads' },
                  { emoji: '📸', name: 'Paid Instagram Ads', desc: 'Visual storytelling → Engagement → Shoppable posts' },
                  { emoji: '📱', name: 'Mobile Conquesting', desc: 'Geo-fencing for awareness → Retargeting for conversion' },
                ].map(ch => (
                  <div key={ch.name} className="bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{ch.emoji}</span>
                      <span className="text-white font-medium text-sm">{ch.name}</span>
                    </div>
                    <p className="text-slate-400 text-xs">{ch.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Key Insight */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-xl p-5 border border-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">💡</span>
              <h3 className="text-lg font-semibold text-white">The Key Insight</h3>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              <strong className="text-white">A balanced marketing mix spans all three funnel stages.</strong> Many businesses 
              over-invest in bottom-of-funnel (Google Ads) while neglecting the top (brand awareness). But here's the truth: 
              you can't convert someone who's never heard of you. <strong className="text-white">Reach + Frequency = Results.</strong> 
              Build your audience at the top, nurture them in the middle, and they'll be ready when they need you at the bottom.
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
